import { ISpinnerStyles } from '@fluentui/react/lib/Spinner';

import { ComponentClassNames } from '../../util/classNames';
import { addAlpha } from '../../util/colors';
import { NumericFontSize, getFluentLineHeightTokenFromNumericFontSize } from '../../util/styles/fluent9';
import { getGutterValue } from '../../util/styles/gutters';
import { ITheme, memoizeFunction, mergeStyleSets } from '../../util/styling';

import { SpinnerColor, SpinnerSize, TextPosition } from './Spinner.types';

const horizontalLabelMargins: Record<SpinnerSize, string> = {
  xSmall: `0 0 0 ${getGutterValue(1.5, true)}`,
  small: `0 0 0 ${getGutterValue(2, true)}`,
  medium: `-1px 0 0 ${getGutterValue(2, true)}`,
  large: `0 0 0 ${getGutterValue(2, true)}`,
};

const verticalLabelMargins: Record<SpinnerSize, string> = {
  ...horizontalLabelMargins,
  large: `${getGutterValue(1, true)} 0 0`,
};

const getLabelMargins = (textPosition: TextPosition) => {
  if (textPosition === 'below') {
    return verticalLabelMargins;
  }

  return horizontalLabelMargins;
};

type SpinnerClassNames = ComponentClassNames<'spinner' | 'label'>;

export const getClassNames = memoizeFunction(
  (
    size: SpinnerSize,
    isCentered: boolean,
    textSize: NumericFontSize,
    textPosition: TextPosition,
    className?: string
  ): SpinnerClassNames => {
    const lineHeight = getFluentLineHeightTokenFromNumericFontSize(textSize);

    return mergeStyleSets({
      spinner: [
        {
          display: 'flex',
          alignItems: 'center',
          flexDirection: textPosition === 'below' ? 'column' : 'row',
          minHeight: lineHeight,
          justifyContent: isCentered ? 'center' : undefined,
        },
        className,
      ],
      label: {
        margin: getLabelMargins(textPosition)[size],
      },
    });
  }
);

const getFluentSpinnerColor = (theme: ITheme): Record<SpinnerColor, string> => ({
  light: theme.palette.neutralDark,
  metadata: theme.palette.neutralDark,
  light_blue: theme.palette.accent,
  dark: theme.palette.white,
});

export const getFluentSpinnerStyles = memoizeFunction((color: SpinnerColor, theme: ITheme): ISpinnerStyles => {
  const fluentSpinnerColor = getFluentSpinnerColor(theme)[color];

  return {
    circle: {
      borderWidth: '2px',
      borderColor: `${addAlpha(fluentSpinnerColor, 100)} ${addAlpha(fluentSpinnerColor, 30)} ${addAlpha(
        fluentSpinnerColor,
        30
      )}`,
    },
  };
});
